import {NavigationPageEnum, NavigationPageType} from './navigations.enums';
import {AccessCodeBaseEnum, AccessCodeBaseType, AccessCodeBitEnum, AccessCodeBitType} from './access-code.enum';

//#region INTERFACE IFeature
/**
 * Represents a single feature configuration, including its access control metadata.
 *
 * @interface IFeature
 * @description This interface defines the configuration for a feature, including its unique identifier and the associated access control codes.
 */
export interface IFeature {
  /**
   * The unique identifier of the feature.
   *
   * @type {NavigationPageType}
   * @example NavigationPage.home
   */
  feature: NavigationPageType;

  /**
   * The base access code(s) associated with the feature.
   * These are the core permissions required to access the feature.
   *
   * @type {AccessCodeBaseType[]}
   * @example [AccessCodeBaseEnum.StockView, AccessCodeBaseEnum.DashboardView]
   */
  accessCodeBase?: AccessCodeBaseType[];

  /**
   * The specific bit(s) in the access code representing permissions for the feature.
   * These bits determine the exact level of access for the feature.
   *
   * @type {AccessCodeBitType[]}
   * @example [AccessCodeBitEnum.StockView, AccessCodeBitEnum.DashboardView]
   */
  accessCodeBit?: AccessCodeBitType[];
}

//#endregion

//#region CONST Features
/**
 * Dictionary of feature configurations indexed by their feature name.
 *
 * @const Features
 * @description A mapping of feature names (as keys) to their configuration (as values). This helps manage and access feature configurations based on the feature name.
 * @type {Record<string, IFeature>}
 * @example
 * const homeFeature = Features[NavigationPage.home];
 */
export const Features: { [key: string]: IFeature } = {
  [NavigationPageEnum.Home]: {
    feature: NavigationPageEnum.Home,
    accessCodeBase: [],
    accessCodeBit: [],
  },
  [NavigationPageEnum.Login]: {
    feature: NavigationPageEnum.Login,
    accessCodeBase: [],
    accessCodeBit: [],
  },
  [NavigationPageEnum.NGPReport]: {
    feature: NavigationPageEnum.NGPReport,
    accessCodeBase: [],
    accessCodeBit: [],
  },
  [NavigationPageEnum.Quotes]: {
    feature: NavigationPageEnum.Quotes,
    accessCodeBase: [],
    accessCodeBit: [],
  },
  [NavigationPageEnum.AutoOrdering]: {
    feature: NavigationPageEnum.AutoOrdering,
    accessCodeBase: [],
    accessCodeBit: [],
  },
  [NavigationPageEnum.BarcodeScannerPageBinLocationsUpdate]: {
    feature: NavigationPageEnum.BarcodeScannerPageBinLocationsUpdate,
    accessCodeBase: [],
    accessCodeBit: [],
  },
  [NavigationPageEnum.BarcodeScannerPageHome]: {
    feature: NavigationPageEnum.BarcodeScannerPageHome,
    accessCodeBase: [],
    accessCodeBit: [],
  },
  [NavigationPageEnum.BarcodeScannerPageOrderCreate]: {
    feature: NavigationPageEnum.BarcodeScannerPageOrderCreate,
    accessCodeBase: [],
    accessCodeBit: [],
  },
  [NavigationPageEnum.BarcodeScannerPageProcessGRV]: {
    feature: NavigationPageEnum.BarcodeScannerPageProcessGRV,
    accessCodeBase: [],
    accessCodeBit: [],
  },
  [NavigationPageEnum.BarcodeScannerPageShelfTalkersPrint]: {
    feature: NavigationPageEnum.BarcodeScannerPageShelfTalkersPrint,
    accessCodeBase: [AccessCodeBaseEnum.BarcodeScannerHome],
    accessCodeBit: [AccessCodeBitEnum.BarcodeScannerHome],
  },
  [NavigationPageEnum.BarcodeScannerPageStockUpdate]: {
    feature: NavigationPageEnum.BarcodeScannerPageStockUpdate,
    accessCodeBase: [],
    accessCodeBit: [],
  },
  [NavigationPageEnum.Dashboard]: {
    feature: NavigationPageEnum.Dashboard,
    accessCodeBase: [],
    accessCodeBit: [],
  },
  [NavigationPageEnum.DashboardReporting]: {
    feature: NavigationPageEnum.DashboardReporting,
    accessCodeBase: [],
    accessCodeBit: [],
  },
  [NavigationPageEnum.StockTake]: {
    feature: NavigationPageEnum.StockTake,
    accessCodeBase: [],
    accessCodeBit: [],
  },
  [NavigationPageEnum.StockManager]: {
    feature: NavigationPageEnum.StockManager,
    accessCodeBase: [],
    accessCodeBit: [],
  },
  [NavigationPageEnum.ProductMapping]: {
    feature: NavigationPageEnum.ProductMapping,
    accessCodeBase: [],
    accessCodeBit: [],
  },
  [NavigationPageEnum.Settings]: {
    feature: NavigationPageEnum.Settings,
    accessCodeBase: [],
    accessCodeBit: [],
  },
  [NavigationPageEnum.SettingsNew]: {
    feature: NavigationPageEnum.SettingsNew,
    accessCodeBase: [],
    accessCodeBit: [],
  },
  [NavigationPageEnum.ShelfTalkers]: {
    feature: NavigationPageEnum.ShelfTalkers,
    accessCodeBase: [],
    accessCodeBit: [],
  }, [NavigationPageEnum.UserAccess]: {
    feature: NavigationPageEnum.UserAccess,
    accessCodeBase: [],
    accessCodeBit: [],
  },
};

//#endregion
